/**
 * Auto-layout for a family tree.
 *
 * Approach:
 *  1. Compute a generation depth for each person by walking parent → child edges.
 *     Roots (no parents) start at generation 0. A child's generation is
 *     max(parent_gen) + 1. Anyone unreachable defaults to their own row.
 *  2. Spouse rows: if two people are spouses and one has a generation while
 *     the other doesn't, pull the missing one to the same generation.
 *  3. Within each generation, place spouse pairs adjacent, then singletons.
 *  4. X positions are computed on a fixed column pitch so the SVG canvas
 *     can size itself deterministically.
 *
 * Output shape:
 *  { positions: { [personId]: { x, y, gen } }, width, height, genCount }
 */

const COL_W = 210;       // horizontal pitch between people
const ROW_H = 230;       // vertical pitch between generations
const PAD_X = 60;        // horizontal padding
const PAD_Y = 60;        // vertical padding
const SPOUSE_GAP = 190;  // horizontal gap between spouses in a couple

export function layoutTree(people, relationships) {
  if (!people.length) {
    return { positions: {}, width: 720, height: 420, genCount: 0 };
  }
  const byId = Object.fromEntries(people.map((p) => [p.id, p]));

  // Parent edges (from = parent, to = child)
  const parentEdges = relationships.filter((r) => r.type === "parent");
  const spouseEdges = relationships.filter((r) => r.type === "spouse");

  // parents[childId] = [parentIds]
  const parentsOf = {};
  parentEdges.forEach((r) => {
    (parentsOf[r.to_id] = parentsOf[r.to_id] || []).push(r.from_id);
  });
  // children[parentId] = [childIds]
  const childrenOf = {};
  parentEdges.forEach((r) => {
    (childrenOf[r.from_id] = childrenOf[r.from_id] || []).push(r.to_id);
  });
  // spouseOf[a] = b
  const spouseOf = {};
  spouseEdges.forEach((r) => {
    spouseOf[r.from_id] = r.to_id;
    spouseOf[r.to_id] = r.from_id;
  });

  // 1) BFS from roots (no parents) to assign generations
  const gen = {};
  const roots = people.filter((p) => !(parentsOf[p.id] && parentsOf[p.id].length));
  const queue = [];
  roots.forEach((p) => {
    gen[p.id] = 0;
    queue.push(p.id);
  });
  // if no roots (shouldn't happen since parent cycles are blocked server-side)
  if (!queue.length && people.length) {
    gen[people[0].id] = 0;
    queue.push(people[0].id);
  }
  while (queue.length) {
    const id = queue.shift();
    const kids = childrenOf[id] || [];
    kids.forEach((cid) => {
      const cand = (gen[id] ?? 0) + 1;
      if (gen[cid] === undefined || cand > gen[cid]) {
        gen[cid] = cand;
        queue.push(cid);
      }
    });
  }
  // 2) spouses share a generation
  for (let iter = 0; iter < 3; iter += 1) {
    people.forEach((p) => {
      const sp = spouseOf[p.id];
      if (!sp) return;
      if (gen[p.id] !== undefined && gen[sp] === undefined) gen[sp] = gen[p.id];
      if (gen[sp] !== undefined && gen[p.id] === undefined) gen[p.id] = gen[sp];
    });
  }
  // Anyone still ungenerated → parked on a bottom row
  const maxGen = people.reduce((m, p) => Math.max(m, gen[p.id] ?? 0), 0);
  people.forEach((p) => {
    if (gen[p.id] === undefined) gen[p.id] = maxGen + 1;
  });

  // 3) Group by generation, pair spouses side-by-side
  const genGroups = {};
  people.forEach((p) => {
    (genGroups[gen[p.id]] = genGroups[gen[p.id]] || []).push(p);
  });

  const positions = {};
  const rowGens = Object.keys(genGroups).map(Number).sort((a, b) => a - b);
  let maxCols = 1;

  rowGens.forEach((g) => {
    const row = genGroups[g].slice();
    // Sort by created_at for stable order within a row
    row.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

    // Build "units": either a spouse-pair (two people) or a single person.
    const placed = new Set();
    const units = [];
    row.forEach((p) => {
      if (placed.has(p.id)) return;
      const sp = spouseOf[p.id];
      if (sp && !placed.has(sp) && byId[sp] && gen[sp] === g) {
        // spouse pair — order: put the earlier-created first
        const partner = byId[sp];
        const pair = [p, partner].sort(
          (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
        );
        units.push({ type: "pair", people: pair });
        placed.add(p.id);
        placed.add(sp);
      } else {
        units.push({ type: "single", people: [p] });
        placed.add(p.id);
      }
    });

    // Layout the units
    const rowWidth = units.reduce((acc, u) => {
      return acc + (u.type === "pair" ? SPOUSE_GAP + COL_W : COL_W);
    }, 0);
    let cursor = PAD_X;
    // If the row is narrower than the widest row so far, we still place
    // starting from PAD_X — the SVG canvas will grow to fit the max.
    units.forEach((u) => {
      if (u.type === "pair") {
        positions[u.people[0].id] = {
          x: cursor + COL_W / 2,
          y: PAD_Y + g * ROW_H,
          gen: g,
        };
        positions[u.people[1].id] = {
          x: cursor + COL_W / 2 + SPOUSE_GAP,
          y: PAD_Y + g * ROW_H,
          gen: g,
        };
        cursor += SPOUSE_GAP + COL_W + 40;
      } else {
        positions[u.people[0].id] = {
          x: cursor + COL_W / 2,
          y: PAD_Y + g * ROW_H,
          gen: g,
        };
        cursor += COL_W + 40;
      }
    });
    maxCols = Math.max(maxCols, Math.ceil(rowWidth / COL_W));
    void rowWidth;
  });

  // Compute canvas size
  const xs = Object.values(positions).map((p) => p.x);
  const ys = Object.values(positions).map((p) => p.y);
  const width = Math.max(720, Math.max(...xs) + PAD_X + COL_W / 2);
  const height = Math.max(420, Math.max(...ys) + PAD_Y + ROW_H / 2);

  return { positions, width, height, genCount: rowGens.length };
}

export const LAYOUT_CONSTANTS = { COL_W, ROW_H, PAD_X, PAD_Y, SPOUSE_GAP };
